import Assignment from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlightIcon from '@mui/icons-material/Flight';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import {
    Alert,
    Box,
    Card,
    Switch,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import useFlightLookup from 'hooks/useFlightLookup';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import theme from '../../../../app/styles/themes/advantage';
import { prefetchHereFlyoverTilesForRoute } from '../../../../components/Common/HereMaps/hereMaps';
import { useBookingWizardContext } from '../hooks/useBookingWizard';

// ============================================================================
// HELPERS
// ============================================================================

export const looksLikeAirport = (location: any): boolean => {
  if (!location) return false
  if (location?.pointOfInterestTypeName === 'Airport') return true
  if (location?.id?.pointOfInterestTypeName === 'Airport') return true
  const addr = (
    location?.formattedAddress ||
    location?.id?.formattedAddress ||
    location?.addressLine1 ||
    location?.id?.addressLine1 ||
    ''
  ).toLowerCase()
  return /airport|\([a-z]{3}\)/i.test(addr)
}

const toLocalDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`

const dateValueToStr = (when: any): string | undefined => {
  if (!when) return undefined
  if (when instanceof Date) return toLocalDateStr(when)
  if (typeof when === 'string') return when.slice(0, 10)
  return undefined
}

const seedPickupFromArrival = (
  arrivalIso: string,
  bufferMinutes = 30,
): Date | null => {
  const arrival = DateTime.fromISO(arrivalIso, { setZone: true })
  if (!arrival.isValid) return null
  const local = arrival.plus({ minutes: bufferMinutes })
  return new Date(
    local.year,
    local.month - 1,
    local.day,
    local.hour,
    local.minute,
    local.second,
  )
}

const formatFlightTime = (
  iso: string | undefined,
  timeZone: string | undefined,
): string => {
  if (!iso) return ''
  const dt = DateTime.fromISO(iso, { setZone: true })
  if (!dt.isValid) return iso
  return `${dt.toFormat('HH:mm')} (${timeZone?.replace(/_/g, ' ') ?? ''})`
}

const locationLabel = (location: any): string => {
  if (!location) return ''
  const formatted = location?.formattedAddress || location?.id?.formattedAddress
  if (typeof formatted === 'string' && formatted.trim()) {
    return formatted.split(',')[0]?.trim() || formatted
  }
  return (
    location?.addressLine1 ||
    location?.id?.addressLine1 ||
    location?.name ||
    location?.id?.name ||
    ''
  )
}

// ============================================================================
// STYLED
// ============================================================================

const SummaryCard = styled(Card)(({ theme }) => ({
  padding: '14px 16px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.brand.dark,
    backgroundColor: theme.palette.action.hover,
  },
}))

const ExpandedCard = styled(Card)(({ theme }) => ({
  padding: '16px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  borderRadius: 8,
}))

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginTop: '14px',
  marginBottom: '8px',
  '&:first-of-type': { marginTop: 0 },
}))

const FlightCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  marginBottom: '8px',
  overflow: 'hidden',
  '&:last-of-type': { marginBottom: 0 },
}))

const FlightCardSection = styled(Box)({
  display: 'flex',
  gap: '10px',
  padding: '10px 12px',
  alignItems: 'flex-start',
})

const FlightCardDivider = styled(Box)(({ theme }) => ({
  height: 1,
  background: theme.palette.divider,
  margin: '0 12px',
}))

const LegIconWrap = styled(Box)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: theme.palette.action.hover,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 2,
}))

const ServiceRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '12px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  marginBottom: '8px',
  '&:last-of-type': { marginBottom: 0 },
}))

const ServiceIconWrap = styled(Box)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: 6,
  background: theme.palette.action.hover,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: theme.palette.brand.dark,
}))

// ============================================================================
// COMPONENT
// ============================================================================

interface LegContext {
  key: 'outbound' | 'inbound'
  pickup: any
  dropOff: any
  pickupIsAirport: boolean
  dropOffIsAirport: boolean
  flightField: string
  servicesField: string
  whenField: string
  timeZoneField: string
  flight?: string
  services?: string
  when?: any
  routeLabel: string
}

interface LegFlightRowProps {
  leg: LegContext
  flightInfo: any
  flightLoading: boolean
  onChange: (value: string) => void
}

interface AirportServicesProps {
  isReturnJourney: boolean
}

function LegFlightRow({
  leg,
  flightInfo,
  flightLoading,
  onChange,
}: LegFlightRowProps) {
  const isArriving = leg.pickupIsAirport
  const isDeparting = leg.dropOffIsAirport && !leg.pickupIsAirport
  const directionLabel = isArriving ? 'Arriving flight' : 'Departing flight'
  const directionColor = isArriving ? 'success.dark' : 'warning.dark'
  const Icon = isArriving ? FlightLandIcon : FlightTakeoffIcon
  const placeholder = isArriving
    ? 'e.g. BA117 - flight you arrive on'
    : 'e.g. BA118 - flight you catch'

  return (
    <FlightCardSection>
      <LegIconWrap>
        <Icon
          sx={{
            fontSize: 16,
            color: isArriving ? 'success.main' : 'warning.main',
          }}
        />
      </LegIconWrap>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* <Typography
          sx={(theme) => ({
            fontSize: '12px',
            color: theme.palette.text.secondary,
            lineHeight: 1.4,
            marginBottom: '4px',
          })}
        >
          {leg.routeLabel}
        </Typography> */}
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            color: directionColor,
            marginBottom: '6px',
          }}
        >
          {directionLabel}
        </Typography>

        <TextField
          size="small"
          fullWidth
          placeholder={placeholder}
          value={leg.flight || ''}
          onChange={(e) => onChange(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '14px',
            },
          }}
        />

        {flightLoading && (
          <Typography
            sx={(theme) => ({
              fontSize: '11px',
              color: theme.palette.text.secondary,
              marginTop: '4px',
            })}
          >
            Looking up flight…
          </Typography>
        )}

        {flightInfo && !flightLoading && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon fontSize="small" />}
            sx={{
              fontSize: '12px',
              py: 0.5,
              marginTop: '6px',
              border: 1,
              borderColor: theme.palette.success.light,
            }}
          >
            <Box sx={{ fontWeight: 600 }}>
              {flightInfo.departure?.iata} {'>'} {flightInfo.arrival?.iata}
              {flightInfo.status ? ` · ${flightInfo.status}` : ''}
            </Box>
            {flightInfo.arrival?.terminal && isArriving && (
              <Box sx={{ fontSize: '11px', mt: 0.25 }}>
                Terminal {flightInfo.arrival.terminal}
                {flightInfo.arrivalTime
                  ? ` · arrives ${formatFlightTime(
                      flightInfo.arrivalTime,
                      flightInfo.arrival?.timeZone,
                    )}`
                  : ''}
              </Box>
            )}
            {flightInfo.departure?.terminal && !isArriving && (
              <Box sx={{ fontSize: '11px', mt: 0.25 }}>
                Terminal {flightInfo.departure.terminal}
                {flightInfo.departureTime
                  ? ` · departs ${formatFlightTime(
                      flightInfo.departureTime,
                      flightInfo.departure?.timeZone,
                    )}`
                  : ''}
              </Box>
            )}
          </Alert>
        )}

        {isDeparting &&
          !flightInfo &&
          !flightLoading &&
          !leg.flight?.trim() && (
            <Typography
              sx={(theme) => ({
                fontSize: '11px',
                color: theme.palette.text.secondary,
                marginTop: '6px',
                fontStyle: 'italic',
              })}
            >
              We&rsquo;ll use this to suggest a car at your destination airport.
            </Typography>
          )}
      </Box>
    </FlightCardSection>
  )
}

const AirportServices: React.FC<AirportServicesProps> = ({
  isReturnJourney,
}) => {
  const { control, setValue } = useFormContext()

  const outboundPickup = useWatch({ control, name: 'outbound.pickup' })
  const outboundDropOff = useWatch({ control, name: 'outbound.dropOff' })
  const outboundFlight = useWatch({
    control,
    name: 'outbound.flightDetails.flightNumber',
  })
  const outboundServices = useWatch({ control, name: 'outbound.services' })
  const outboundWhen = useWatch({ control, name: 'outbound.when' })

  const inboundFlight = useWatch({
    control,
    name: 'inbound.flightDetails.flightNumber',
  })
  const inboundServices = useWatch({ control, name: 'inbound.services' })
  const inboundWhen = useWatch({ control, name: 'inbound.when' })

  const outboundPickupIsAirport = looksLikeAirport(outboundPickup)
  const outboundDropOffIsAirport = looksLikeAirport(outboundDropOff)

  const outLeg: LegContext = {
    key: 'outbound',
    pickup: outboundPickup,
    dropOff: outboundDropOff,
    pickupIsAirport: outboundPickupIsAirport,
    dropOffIsAirport: outboundDropOffIsAirport,
    flightField: 'outbound.flightDetails.flightNumber',
    servicesField: 'outbound.services',
    whenField: 'outbound.when',
    timeZoneField: 'outbound.timeZone',
    flight: outboundFlight,
    services: outboundServices,
    when: outboundWhen,
    routeLabel: `${locationLabel(outboundPickup)} > ${locationLabel(
      outboundDropOff,
    )}`,
  }

  const inLeg: LegContext | null = isReturnJourney
    ? {
        key: 'inbound',
        pickup: outboundDropOff,
        dropOff: outboundPickup,
        pickupIsAirport: outboundDropOffIsAirport,
        dropOffIsAirport: outboundPickupIsAirport,
        flightField: 'inbound.flightDetails.flightNumber',
        servicesField: 'inbound.services',
        whenField: 'inbound.when',
        timeZoneField: 'inbound.timeZone',
        flight: inboundFlight,
        services: inboundServices,
        when: inboundWhen,
        routeLabel: `${locationLabel(outboundDropOff)} > ${locationLabel(
          outboundPickup,
        )}`,
      }
    : null

  const legs: LegContext[] = inLeg ? [outLeg, inLeg] : [outLeg]
  const airportLegs = legs.filter(
    (l) => l.pickupIsAirport || l.dropOffIsAirport,
  )
  const arrivalLegs = legs.filter((l) => l.pickupIsAirport)
  const isAirportJourney = airportLegs.length > 0

  // Onward bookings (via OnwardMapOverlay) historically seeded the inherited
  // arrival flight into `inbound.flightDetails.flightNumber`. The arriving
  // flight belongs on the outbound (arrival airport = outbound pickup), so
  // migrate once on mount when the situation matches.
  const inheritanceMigratedRef = useRef(false)
  useEffect(() => {
    if (inheritanceMigratedRef.current) return
    if (
      outLeg.pickupIsAirport &&
      !outboundFlight?.trim() &&
      inboundFlight?.trim()
    ) {
      setValue('outbound.flightDetails.flightNumber', inboundFlight)
      setValue('inbound.flightDetails.flightNumber', '')
      inheritanceMigratedRef.current = true
    }
  }, [outLeg.pickupIsAirport, outboundFlight, inboundFlight, setValue])

  const hasAnyConfig = !!(
    outboundServices === 'MeetAndGreet' ||
    inboundServices === 'MeetAndGreet' ||
    outboundFlight?.trim() ||
    inboundFlight?.trim()
  )

  const [expanded, setExpanded] = useState<boolean>(false)
  const autoExpandedRef = useRef(false)

  useEffect(() => {
    if (isAirportJourney && !autoExpandedRef.current && !hasAnyConfig) {
      autoExpandedRef.current = true
      setExpanded(true)
    }
    if (!isAirportJourney) {
      autoExpandedRef.current = false
    }
  }, [isAirportJourney, hasAnyConfig])

  // ===========================================================================
  // FLIGHT LOOKUPS
  // ===========================================================================

  const { flight: outboundFlightInfo, isLoading: outboundFlightLoading } =
    useFlightLookup({
      flightNumber: outboundFlight,
      date: dateValueToStr(outboundWhen),
      isArrival: outLeg.pickupIsAirport,
      enabled: !!outboundFlight?.trim(),
    })

  const { flight: inboundFlightInfo, isLoading: inboundFlightLoading } =
    useFlightLookup({
      flightNumber: inboundFlight,
      date: dateValueToStr(inboundWhen || outboundWhen),
      isArrival: inLeg?.pickupIsAirport ?? false,
      enabled: !!inLeg && !!inboundFlight?.trim(),
    })

  // Push the resolved outbound flight up to the wizard so the map background
  // can draw a dashed arc between the two airports. Only relevant when the
  // dropoff is the airport - that's the path that resolves to onward
  // suggestions at the arrival airport.
  const { setMapFlightArc } = useBookingWizardContext()
  useEffect(() => {
    const dep = outboundFlightInfo?.departure
    const arr = outboundFlightInfo?.arrival
    if (
      !outLeg.dropOffIsAirport ||
      !outboundFlight?.trim() ||
      dep?.latitude == null ||
      dep?.longitude == null ||
      arr?.latitude == null ||
      arr?.longitude == null
    ) {
      setMapFlightArc(undefined)
      return
    }
    setMapFlightArc({
      from: {
        latitude: dep.latitude,
        longitude: dep.longitude,
        name: dep.name,
        iata: dep.iata,
      },
      to: {
        latitude: arr.latitude,
        longitude: arr.longitude,
        name: arr.name,
        iata: arr.iata,
      },
    })
    const apiKey = process.env.REACT_APP_HERE_MAPS_API_KEY
    if (apiKey) {
      prefetchHereFlyoverTilesForRoute({
        from: { lat: dep.latitude, lng: dep.longitude },
        to: { lat: arr.latitude, lng: arr.longitude },
        apiKey,
      })
    }
  }, [
    outboundFlight,
    outboundFlightInfo,
    outLeg.dropOffIsAirport,
    setMapFlightArc,
  ])

  useEffect(() => {
    const arrivalIso = outboundFlightInfo?.arrivalTime
    if (!outLeg.pickupIsAirport || !arrivalIso) return
    const airportTz = outboundFlightInfo?.arrival?.timeZone
    const seeded = seedPickupFromArrival(arrivalIso)
    if (!seeded) return
    setValue('outbound.when', seeded, { shouldDirty: true })
    if (airportTz) {
      setValue('outbound.timeZone', airportTz, { shouldDirty: true })
    }
  }, [
    outboundFlightInfo?.arrivalTime,
    outboundFlightInfo?.arrival?.timeZone,
    outLeg.pickupIsAirport,
    setValue,
  ])

  useEffect(() => {
    const arrivalIso = inboundFlightInfo?.arrivalTime
    if (!inLeg?.pickupIsAirport || !arrivalIso) return
    const airportTz = inboundFlightInfo?.arrival?.timeZone
    const seeded = seedPickupFromArrival(arrivalIso)
    if (!seeded) return
    setValue('inbound.when', seeded, { shouldDirty: true })
    if (airportTz) {
      setValue('inbound.timeZone', airportTz, { shouldDirty: true })
    }
  }, [
    inboundFlightInfo?.arrivalTime,
    inboundFlightInfo?.arrival?.timeZone,
    inLeg?.pickupIsAirport,
    setValue,
  ])

  // ===========================================================================
  // M&G HANDLING
  // ===========================================================================

  const meetGreetEnabled =
    outboundServices === 'MeetAndGreet' || inboundServices === 'MeetAndGreet'

  const setMeetGreetForLeg = (legKey: 'outbound' | 'inbound', on: boolean) => {
    const field =
      legKey === 'outbound' ? 'outbound.services' : 'inbound.services'
    setValue(field, on ? 'MeetAndGreet' : '')
  }

  // ===========================================================================
  // SUMMARY
  // ===========================================================================

  const buildSummary = (): string => {
    const parts: string[] = []
    if (outboundFlight?.trim()) {
      const label = outLeg.pickupIsAirport ? 'Arriving' : 'Departing'
      parts.push(`${label} ${outboundFlight.trim()}`)
    }
    const showInbound =
      !!inboundFlight?.trim() && inboundFlight !== outboundFlight
    if (showInbound) {
      const label = inLeg?.pickupIsAirport ? 'Arriving' : 'Departing'
      parts.push(`${label} ${(inboundFlight as string).trim()}`)
    }
    if (meetGreetEnabled) parts.push('Meet & Greet')
    return parts.length ? parts.join(' · ') : 'Configured'
  }

  // ===========================================================================
  // COLLAPSED STATE
  // ===========================================================================

  if (!expanded) {
    return (
      <SummaryCard onClick={() => setExpanded(true)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {hasAnyConfig ? (
            <CheckCircleIcon
              sx={(theme) => ({
                color: theme.palette.success.main,
                fontSize: 20,
              })}
            />
          ) : (
            <FlightIcon
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontSize: 20,
              })}
            />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
              {hasAnyConfig ? 'Extra services' : '+ Extra services'}
            </Typography>
            <Typography
              sx={(theme) => ({
                fontSize: '12px',
                color: theme.palette.text.secondary,
                marginTop: '2px',
              })}
            >
              {hasAnyConfig ? buildSummary() : 'Meet & Greet, Flight Tracking'}
            </Typography>
          </Box>
        </Box>
      </SummaryCard>
    )
  }

  // ===========================================================================
  // EXPANDED STATE
  // ===========================================================================

  return (
    <ExpandedCard>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          Extra services
        </Typography>
        <Typography
          onClick={() => setExpanded(false)}
          sx={(theme) => ({
            fontSize: '12px',
            color: theme.palette.text.secondary,
            cursor: 'pointer',
            '&:hover': { color: theme.palette.brand.dark },
          })}
        >
          Done
        </Typography>
      </Box>

      {airportLegs.length > 0 && <SectionLabel>Flights</SectionLabel>}

      {airportLegs.map((leg) => {
        const legServices =
          leg.key === 'outbound' ? outboundServices : inboundServices
        return (
          <FlightCard key={leg.key}>
            <LegFlightRow
              leg={leg}
              flightInfo={
                leg.key === 'outbound' ? outboundFlightInfo : inboundFlightInfo
              }
              flightLoading={
                leg.key === 'outbound'
                  ? outboundFlightLoading
                  : inboundFlightLoading
              }
              onChange={(value) => setValue(leg.flightField, value)}
            />
            {leg.pickupIsAirport && (
              <>
                <FlightCardDivider />
                <FlightCardSection>
                  <ServiceIconWrap sx={{ width: 28, height: 28 }}>
                    <Assignment sx={{ fontSize: 16 }} />
                  </ServiceIconWrap>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                      Meet &amp; Greet
                    </Typography>
                    <Typography
                      sx={(theme) => ({
                        fontSize: '11px',
                        color: theme.palette.text.secondary,
                        marginTop: '2px',
                        lineHeight: 1.4,
                      })}
                    >
                      Driver waits at for you with a name board.
                    </Typography>
                  </Box>
                  <Switch
                    size="small"
                    checked={legServices === 'MeetAndGreet'}
                    onChange={(e) =>
                      setMeetGreetForLeg(leg.key, e.target.checked)
                    }
                  />
                </FlightCardSection>
              </>
            )}
          </FlightCard>
        )
      })}

      {/* ===================== STANDALONE M&G (no airport pickup) ========= */}
      {arrivalLegs.length === 0 && (
        <>
          <SectionLabel>Services</SectionLabel>
          <ServiceRow>
            <ServiceIconWrap>
              <Assignment sx={{ fontSize: 18 }} />
            </ServiceIconWrap>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                Meet &amp; Greet
              </Typography>
              <Typography
                sx={(theme) => ({
                  fontSize: '11px',
                  color: theme.palette.text.secondary,
                  marginTop: '2px',
                  lineHeight: 1.4,
                })}
              >
                Driver waits for you with a name board.
              </Typography>
            </Box>
            <Switch
              size="small"
              checked={meetGreetEnabled}
              onChange={(e) => setMeetGreetForLeg('outbound', e.target.checked)}
            />
          </ServiceRow>
        </>
      )}
    </ExpandedCard>
  )
}

export const SpecialInstructions: React.FC = () => {
  const { control, setValue } = useFormContext()
  const value = useWatch({ control, name: 'outbound.specialInstructions' })
  const [open, setOpen] = useState<boolean>(!!value)

  if (!open) {
    return (
      <Typography
        onClick={() => setOpen(true)}
        sx={(theme) => ({
          fontSize: '13px',
          color: theme.palette.brand.dark,
          fontWeight: 500,
          cursor: 'pointer',
          marginTop: '8px',
          display: 'inline-block',
          '&:hover': { textDecoration: 'underline' },
        })}
      >
        + Add special instructions
      </Typography>
    )
  }

  return (
    <Box sx={{ marginTop: '12px' }}>
      <Typography
        sx={(theme) => ({
          fontSize: '12px',
          fontWeight: 600,
          color: theme.palette.text.secondary,
          marginBottom: '4px',
        })}
      >
        Special Instructions
      </Typography>
      <TextField
        multiline
        minRows={3}
        maxRows={6}
        fullWidth
        size="small"
        placeholder="Any extra notes for the driver"
        value={value || ''}
        onChange={(e) =>
          setValue('outbound.specialInstructions', e.target.value)
        }
        inputProps={{ maxLength: 2000 }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            fontSize: '14px',
          },
        }}
      />
    </Box>
  )
}

export default AirportServices
